import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import api from '../utils/api';
import { toast } from 'sonner';
import { Save, Bell, AlertTriangle, Shield, Zap, ServerCrash } from 'lucide-react';

const MonitorAlertSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    ssl_warning_days: 30,
    ssl_critical_days: 7,
    slow_response_threshold_ms: 5000,
    http_error_threshold: 500,
    enable_down_alerts: true,
    enable_ssl_alerts: true,
    enable_slow_response_alerts: true,
    enable_http_error_alerts: true
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings/monitor-alerts');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validate
    if (settings.ssl_warning_days <= settings.ssl_critical_days) {
      toast.error('SSL warning days must be greater than critical days');
      return;
    }

    if (settings.slow_response_threshold_ms <= 0) {
      toast.error('Slow response threshold must be positive');
      return;
    }

    if (settings.http_error_threshold < 400) {
      toast.error('HTTP error threshold must be at least 400');
      return;
    }

    setSaving(true);
    try {
      await api.put('/settings/monitor-alerts', settings);
      toast.success('Monitor alert settings saved successfully!');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Monitor Alert Settings</h1>
        <p className="text-slate-600 mt-2">
          Configure alert thresholds and enable/disable specific alert types for uptime monitors
        </p>
      </div>

      {/* SSL Certificate Alerts */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            SSL Certificate Expiry Alerts
          </CardTitle>
          <CardDescription>
            Get notified when SSL certificates are approaching expiration
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Enable SSL Alerts</p>
              <p className="text-sm text-slate-600">Monitor SSL certificate expiry dates</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_ssl_alerts}
                onChange={(e) => setSettings({ ...settings, enable_ssl_alerts: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {settings.enable_ssl_alerts && (
            <div className="grid grid-cols-2 gap-4 ml-4">
              <div>
                <Label>Warning Threshold (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={settings.ssl_warning_days}
                  onChange={(e) => setSettings({ ...settings, ssl_warning_days: parseInt(e.target.value) })}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Trigger warning alert when SSL expires in this many days
                </p>
              </div>
              <div>
                <Label>Critical Threshold (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={settings.ssl_critical_days}
                  onChange={(e) => setSettings({ ...settings, ssl_critical_days: parseInt(e.target.value) })}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Trigger critical alert when SSL expires in this many days
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Monitor Down Alerts */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ServerCrash className="w-5 h-5 text-red-600" />
            Monitor Down Alerts
          </CardTitle>
          <CardDescription>
            Get notified immediately when monitors go offline
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Enable Down Alerts</p>
              <p className="text-sm text-slate-600">Alert when monitors fail to respond (CRITICAL)</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_down_alerts}
                onChange={(e) => setSettings({ ...settings, enable_down_alerts: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Slow Response Alerts */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-600" />
            Slow Response Alerts
          </CardTitle>
          <CardDescription>
            Get notified when response times exceed acceptable limits
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Enable Slow Response Alerts</p>
              <p className="text-sm text-slate-600">Alert when response time is too high (WARNING)</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_slow_response_alerts}
                onChange={(e) => setSettings({ ...settings, enable_slow_response_alerts: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {settings.enable_slow_response_alerts && (
            <div className="ml-4">
              <Label>Response Time Threshold (milliseconds)</Label>
              <Input
                type="number"
                min="100"
                step="100"
                value={settings.slow_response_threshold_ms}
                onChange={(e) => setSettings({ ...settings, slow_response_threshold_ms: parseInt(e.target.value) })}
                className="mt-1 max-w-xs"
              />
              <p className="text-xs text-slate-500 mt-1">
                Alert when response time exceeds this threshold (e.g., 5000 = 5 seconds)
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* HTTP Error Alerts */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            HTTP Error Alerts
          </CardTitle>
          <CardDescription>
            Get notified when monitors return server error status codes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Enable HTTP Error Alerts</p>
              <p className="text-sm text-slate-600">Alert on 500+ status codes (CRITICAL)</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.enable_http_error_alerts}
                onChange={(e) => setSettings({ ...settings, enable_http_error_alerts: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {settings.enable_http_error_alerts && (
            <div className="ml-4">
              <Label>HTTP Status Code Threshold</Label>
              <Input
                type="number"
                min="400"
                max="599"
                value={settings.http_error_threshold}
                onChange={(e) => setSettings({ ...settings, http_error_threshold: parseInt(e.target.value) })}
                className="mt-1 max-w-xs"
              />
              <p className="text-xs text-slate-500 mt-1">
                Alert when status code is >= this value (500 = Internal Server Error, 400 = Bad Request)
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          {settings.updated_at && (
            <p>Last updated: {new Date(settings.updated_at).toLocaleString()}</p>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700"
          size="lg"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <Bell className="w-4 h-4" />
          How Monitor Alerts Work
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Monitors are checked every 30 seconds automatically</li>
          <li>• Alerts are created when thresholds are exceeded</li>
          <li>• Email notifications are sent immediately (if SMTP configured)</li>
          <li>• Alerts auto-resolve when conditions return to normal</li>
          <li>• All alerts appear in the Alerts dashboard</li>
        </ul>
      </div>
    </div>
  );
};

export default MonitorAlertSettings;
